import { useState, useEffect, useMemo } from 'react'
import { supabaseGetter } from '~/api/supabase-getter'

/**
 * Fetches all 4 relationship tables and builds classifiers for the inspector panel.
 *
 * @param {Object} params
 * @param {Array} params.crops - Parent-only crop array
 * @returns {{ classifyCropDetailed, computeRelationshipCounts, loading, error }}
 */
export const useCropRelationships = ({ crops }) => {
  const [cropDetailsMap, setCropDetailsMap] = useState(new Map())
  const [templateCompanionPairs, setTemplateCompanionPairs] = useState(new Set())
  const [templateIncompatiblePairs, setTemplateIncompatiblePairs] = useState(new Set())
  const [familyCompanionPairs, setFamilyCompanionPairs] = useState(new Set())
  const [familyIncompatiblePairs, setFamilyIncompatiblePairs] = useState(new Set())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const cropIds = useMemo(() => (crops || []).map(c => c.id), [crops])

  useEffect(() => {
    if (!crops || crops.length === 0) {
      setCropDetailsMap(new Map())
      setTemplateCompanionPairs(new Set())
      setTemplateIncompatiblePairs(new Set())
      setFamilyCompanionPairs(new Set())
      setFamilyIncompatiblePairs(new Set())
      setLoading(false)
      setError(null)
      return
    }

    async function fetchRelationships() {
      setLoading(true)
      setError(null)

      try {
        // Step 1: fetch crop_details for all crops → get template_id + family_code
        const { data: detailsData, error: detailsErr } =
          await supabaseGetter.cropDetails.getByCropIds(cropIds)
        if (detailsErr) throw detailsErr

        const detailsMap = new Map()
        for (const d of (detailsData || [])) {
          detailsMap.set(d.crop_id, d)
        }

        const templateIds = [...new Set(
          (detailsData || []).map(d => d.template_id).filter(Boolean)
        )]
        const familyCodes = [...new Set(
          (detailsData || []).map(d => d.family_code).filter(Boolean)
        )]

        // Step 2: fetch all 4 relationship tables in parallel
        const [
          { data: tmplCompanions, error: tcErr },
          { data: tmplIncompatibles, error: tiErr },
          { data: famCompanions, error: fcErr },
          { data: famIncompatibles, error: fiErr },
        ] = await Promise.all([
          supabaseGetter.cropTemplateCompanions.getByTemplateIds(templateIds),
          supabaseGetter.cropTemplateIncompatibles.getByTemplateIds(templateIds),
          supabaseGetter.cropFamilyCompanions.getByFamilyCodes(familyCodes),
          supabaseGetter.cropFamilyIncompatibles.getByFamilyCodes(familyCodes),
        ])

        if (tcErr) throw tcErr
        if (tiErr) throw tiErr
        if (fcErr) throw fcErr
        if (fiErr) throw fiErr

        // Build normalised pair sets using sorted key to deduplicate bidirectionality
        const makePair = (a, b) => [a, b].sort().join('|')

        const tcPairs = new Set(
          (tmplCompanions || []).map(r => makePair(r.template_id, r.companion_id))
        )
        const tiPairs = new Set(
          (tmplIncompatibles || []).map(r => makePair(r.template_id, r.incompatible_id))
        )
        const fcPairs = new Set(
          (famCompanions || []).map(r => makePair(r.family_code, r.companion_code))
        )
        const fiPairs = new Set(
          (famIncompatibles || []).map(r => makePair(r.family_code, r.incompatible_code))
        )

        setCropDetailsMap(detailsMap)
        setTemplateCompanionPairs(tcPairs)
        setTemplateIncompatiblePairs(tiPairs)
        setFamilyCompanionPairs(fcPairs)
        setFamilyIncompatiblePairs(fiPairs)
      } catch (err) {
        console.error('Error fetching crop relationships:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchRelationships()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cropIds.join(',')])

  /**
   * Classify the relationship between two crops with full detail.
   * Priority (first match wins):
   *   1. template-incompatible
   *   2. template-companion
   *   3. family-incompatible
   *   4. family-companion
   *   5. same-family
   *   6. neutral
   *
   * @param {string} cropAId
   * @param {string} cropBId
   * @returns {'template-incompatible'|'template-companion'|'family-incompatible'|'family-companion'|'same-family'|'neutral'}
   */
  const classifyCropDetailed = useMemo(() => (cropAId, cropBId) => {
    if (!cropAId || !cropBId || cropAId === cropBId) return 'neutral'

    const aDetails = cropDetailsMap.get(cropAId)
    const bDetails = cropDetailsMap.get(cropBId)

    if (!aDetails || !bDetails) return 'neutral'

    const aTemplate = aDetails.template_id
    const bTemplate = bDetails.template_id
    const aFamily = aDetails.family_code
    const bFamily = bDetails.family_code

    if (aTemplate && bTemplate) {
      const pair = [aTemplate, bTemplate].sort().join('|')
      if (templateIncompatiblePairs.has(pair)) return 'template-incompatible'
      if (templateCompanionPairs.has(pair)) return 'template-companion'
    }

    if (aFamily && bFamily) {
      const pair = [aFamily, bFamily].sort().join('|')
      if (familyIncompatiblePairs.has(pair)) return 'family-incompatible'
      if (familyCompanionPairs.has(pair)) return 'family-companion'
      if (aFamily === bFamily) return 'same-family'
    }

    return 'neutral'
  }, [cropDetailsMap, templateIncompatiblePairs, templateCompanionPairs, familyIncompatiblePairs, familyCompanionPairs])

  /**
   * Count unique pairs for each of the 5 relationship types among a list of crops.
   *
   * @param {Array} cropsList
   * @returns {{ templateCompanion, familyCompanion, sameFamily, familyIncompatible, templateIncompatible }}
   */
  const computeRelationshipCounts = useMemo(() => (cropsList) => {
    let templateCompanion = 0, familyCompanion = 0, sameFamily = 0
    let familyIncompatible = 0, templateIncompatible = 0

    for (let i = 0; i < cropsList.length; i++) {
      for (let j = i + 1; j < cropsList.length; j++) {
        const rel = classifyCropDetailed(cropsList[i].id, cropsList[j].id)
        if      (rel === 'template-companion')    templateCompanion++
        else if (rel === 'family-companion')      familyCompanion++
        else if (rel === 'same-family')           sameFamily++
        else if (rel === 'family-incompatible')   familyIncompatible++
        else if (rel === 'template-incompatible') templateIncompatible++
      }
    }

    return { templateCompanion, familyCompanion, sameFamily, familyIncompatible, templateIncompatible }
  }, [classifyCropDetailed])

  /**
   * Classify the relationship between an existing crop and a candidate template.
   * Useful for filtering template lists by relationship to an active crop.
   *
   * @param {string} cropId - ID of the existing crop
   * @param {{ id: string, family_code: string }} template - Candidate template object
   * @returns {'template-incompatible'|'template-companion'|'family-incompatible'|'family-companion'|'same-family'|'neutral'}
   */
  const classifyTemplateForCrop = useMemo(() => (cropId, template) => {
    const cropDetail = cropDetailsMap.get(cropId)
    if (!cropDetail || !template) return 'neutral'

    const existingTemplateId = cropDetail.template_id
    const existingFamilyCode = cropDetail.family_code
    const newTemplateId = template.id
    const newFamilyCode = template.family_code

    if (existingTemplateId && newTemplateId) {
      const pair = [existingTemplateId, newTemplateId].sort().join('|')
      if (templateIncompatiblePairs.has(pair)) return 'template-incompatible'
      if (templateCompanionPairs.has(pair))    return 'template-companion'
    }

    if (existingFamilyCode && newFamilyCode) {
      const pair = [existingFamilyCode, newFamilyCode].sort().join('|')
      if (familyIncompatiblePairs.has(pair))    return 'family-incompatible'
      if (familyCompanionPairs.has(pair))       return 'family-companion'
      if (existingFamilyCode === newFamilyCode) return 'same-family'
    }

    return 'neutral'
  }, [cropDetailsMap, templateIncompatiblePairs, templateCompanionPairs, familyIncompatiblePairs, familyCompanionPairs])

  return { classifyCropDetailed, classifyTemplateForCrop, computeRelationshipCounts, loading, error }
}
